// «Send e-post manuelt»: velg en mal, rett den, velg mottakere og send.
//
// Samme flyt og samme API-kall som på den klassiske ordresiden
// (OrderDetails/OrderDetail.js), samlet i én komponent så den nye ordresiden
// slipper å sende admin dit for å sende en e-post. Den klassiske siden har
// fortsatt sin egen kopi, vevd inn i resten av siden.
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { notifyCenter } from "utils/toast";
import {
  getEmailTemplate,
  editEmailTemplate,
  sendManualEmail,
  changeStatusWithoutEmail,
  fetchStatusList,
} from "api/apiService";

// Soft UI-temaet tvinger display:grid og width:max-content på Select, så bare
// teksten blir klikkbar. Samme overstyring som i StatusChangeDialog.
const SELECT_SX = {
  minHeight: 42,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "42px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

const ADMIN_EMAIL = "post@betongsentrum.no";
const ROLES = [
  ["customer", "Kunde"],
  ["vendor", "Produsent"],
  ["admin", "Admin"],
];

const VARIABLES =
  "{{customer_name}}, {{vendor_name}}, {{project_link_customer}}, {{project_link_vendor}}, {{order_invoice}}, {{order_payment_link}}, {{order_payment_button}}, {{delivery_day}}, {{delivery_date}}, {{delivery_time}}, {{customer_phone_number}}, {{admin_phone_number}}, {{volume}}, {{concrete_quality}}, {{truck_type}}, {{delivery_address}}";

/** Komma, linjeskift, semikolon eller mellomrom skiller adressene. Uten duplikater. */
function parseEmails(text = "") {
  const seen = new Map();
  String(text)
    .split(/[\n,; \t]+/g)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((e) => {
      if (!seen.has(e.toLowerCase())) seen.set(e.toLowerCase(), e);
    });
  return Array.from(seen.values());
}

const EMPTY = { templateId: "", template_name: "", email_type: "", subject: "", message: "", roles: [], to: "" };

function ManualEmailDialog({ open, orderId, currentStatus, customerEmail, vendorEmail, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [nextStatus, setNextStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const roleEmail = useMemo(
    () => ({
      customer: String(customerEmail || "").trim(),
      vendor: String(vendorEmail || "").trim(),
      admin: ADMIN_EMAIL,
    }),
    [customerEmail, vendorEmail]
  );

  useEffect(() => {
    if (!open) {
      setForm(EMPTY);
      setNextStatus("");
      return;
    }
    getEmailTemplate({ limit: -1, page_number: 1 })
      .then((res) => {
        if (res?.status && Array.isArray(res.data)) {
          setTemplates([...res.data].sort((a, b) => a.id - b.id));
        }
      })
      .catch(() => notifyCenter("error", "Kunne ikke hente e-postmalene."));
    fetchStatusList()
      .then((res) => {
        // Kun statusene i løpet: valget endrer ordren.
        if (Array.isArray(res?.status_list)) setStatuses(res.status_list.filter((s) => !s.legacy));
      })
      .catch(() => {});
  }, [open]);

  const pickTemplate = async (templateId) => {
    if (!templateId) {
      setForm(EMPTY);
      return;
    }
    setLoading(true);
    try {
      const res = await editEmailTemplate(templateId);
      if (!res?.status || !res.data) {
        notifyCenter("error", "Kunne ikke hente malen.");
        return;
      }
      const t = res.data;
      const roles = Array.isArray(t.send_to) ? t.send_to : [];
      setForm({
        templateId,
        template_name: t.template_name || "",
        email_type: t.email_type || "",
        subject: t.subject || "",
        message: t.message || "",
        roles,
        // Mottakerne malen er satt opp med, fylt inn fra ordren.
        to: parseEmails(roles.map((r) => roleEmail[r]).join(",")).join(",\n"),
      });
    } catch (e) {
      notifyCenter("error", "Kunne ikke hente malen.");
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (role) => {
    setForm((prev) => {
      const selected = prev.roles.includes(role);
      const roles = selected ? prev.roles.filter((r) => r !== role) : [...prev.roles, role];
      const email = roleEmail[role];
      let list = parseEmails(prev.to);
      if (email) {
        if (!selected) {
          if (!list.some((e) => e.toLowerCase() === email.toLowerCase())) list.push(email);
        } else if (!roles.some((r) => (roleEmail[r] || "").toLowerCase() === email.toLowerCase())) {
          // Fjernes bare hvis ingen av de andre valgte rollene har samme adresse.
          list = list.filter((e) => e.toLowerCase() !== email.toLowerCase());
        }
      }
      return { ...prev, roles, to: list.join(",\n") };
    });
  };

  const send = async () => {
    const recipients = parseEmails(form.to);
    const missing = [];
    if (!form.templateId) missing.push("mal");
    if (!form.subject.trim()) missing.push("emne");
    if (!form.message.trim()) missing.push("innhold");
    if (!recipients.length) missing.push("mottaker");
    if (missing.length) {
      notifyCenter("warning", `Mangler: ${missing.join(", ")}.`);
      return;
    }

    setBusy(true);
    try {
      const res = await sendManualEmail({
        orderId,
        email_type: form.email_type,
        template_name: form.template_name,
        subject: form.subject,
        message: form.message,
        send_to: form.roles,
        send_to_emails: recipients,
        status: nextStatus,
      });
      if (res?.status !== true) {
        notifyCenter("error", res?.msg || res?.message || "Kunne ikke sende e-posten.");
        return;
      }

      // Valgfri statusendring. Uten statusens egen e-post: den admin nettopp
      // skrev ER e-posten.
      if (nextStatus && nextStatus !== currentStatus) {
        const changed = await changeStatusWithoutEmail(orderId, nextStatus).catch(() => null);
        if (changed?.ok === true) notifyCenter("success", "E-posten er sendt, og status er endret.");
        else notifyCenter("warning", changed?.message || "E-posten er sendt, men statusen ble ikke endret.");
      } else {
        notifyCenter("success", "E-posten er sendt.");
      }
      onSent?.();
      onClose?.();
    } catch (e) {
      notifyCenter("error", "Kunne ikke sende e-posten.");
    } finally {
      setBusy(false);
    }
  };

  const label = (text) => (
    <SoftTypography variant="caption" fontWeight="bold" display="block" mt={2} mb={0.5}>
      {text}
    </SoftTypography>
  );

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>Send e-post manuelt</DialogTitle>
      <DialogContent>
        <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          E-postmal
        </SoftTypography>
        <FormControl fullWidth>
          <Select value={form.templateId} displayEmpty sx={SELECT_SX} onChange={(e) => pickTemplate(e.target.value)}>
            <MenuItem value="">Velg mal …</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.template_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {loading && (
          <SoftBox display="flex" alignItems="center" gap={1} mt={2}>
            <CircularProgress size={16} />
            <SoftTypography variant="caption" color="text">
              Henter malen …
            </SoftTypography>
          </SoftBox>
        )}

        {!!form.templateId && !loading && (
          <>
            {label("Emne")}
            <SoftInput value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} />

            {label("Send til")}
            <SoftBox display="flex" gap={0.75} flexWrap="wrap" mb={1}>
              {ROLES.map(([role, text]) => {
                const on = form.roles.includes(role);
                return (
                  <Chip
                    key={role}
                    label={roleEmail[role] ? text : `${text} (mangler e-post)`}
                    color={on ? "info" : "default"}
                    variant={on ? "filled" : "outlined"}
                    onClick={() => toggleRole(role)}
                    aria-pressed={on}
                  />
                );
              })}
            </SoftBox>
            <SoftInput
              multiline
              rows={2}
              placeholder="E-postadresser, skilt med komma eller linjeskift"
              value={form.to}
              onChange={(e) => setForm((p) => ({ ...p, to: e.target.value }))}
              onBlur={() => setForm((p) => ({ ...p, to: parseEmails(p.to).join(",\n") }))}
            />
            <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
              Velg roller for å fylle inn adressene fra ordren. Du kan også skrive inn andre adresser.
            </SoftTypography>

            {label("Statusendring (valgfritt)")}
            <FormControl fullWidth>
              <Select value={nextStatus} displayEmpty sx={SELECT_SX} onChange={(e) => setNextStatus(e.target.value)}>
                <MenuItem value="">Ingen statusendring</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s.value} value={s.value} disabled={s.value === currentStatus}>
                    Endre status til: {s.label}
                    {s.value === currentStatus ? " (nåværende)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {label("Innhold")}
            <ReactQuill
              theme="snow"
              value={form.message}
              onChange={(value) => setForm((p) => ({ ...p, message: value }))}
              style={{ backgroundColor: "#fff" }}
            />
            <SoftBox mt={1.5} p={1.5} sx={{ backgroundColor: "#f4f6f8", borderRadius: 2 }}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Variabler som fylles inn når e-posten sendes
              </SoftTypography>
              <SoftTypography
                variant="caption"
                color="text"
                display="block"
                sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
              >
                {VARIABLES}
              </SoftTypography>
            </SoftBox>
          </>
        )}

        <SoftBox mt={3} display="flex" gap={1} justifyContent="flex-end">
          <SoftButton size="small" variant="text" color="secondary" disabled={busy} onClick={onClose}>
            Avbryt
          </SoftButton>
          <SoftButton
            size="small"
            variant="gradient"
            color="info"
            disabled={busy || loading || !form.templateId}
            onClick={send}
          >
            {busy ? "Sender …" : nextStatus ? "Send e-post og endre status" : "Send e-post"}
          </SoftButton>
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
}

ManualEmailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentStatus: PropTypes.string,
  customerEmail: PropTypes.string,
  vendorEmail: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func,
};

ManualEmailDialog.defaultProps = { currentStatus: "", customerEmail: "", vendorEmail: "", onSent: null };

export default ManualEmailDialog;
